import type { Option } from '@usealloy/component-library';
import type {
  AggregationType,
  TimeframeUnit,
} from 'flow/store/engine/constants';
import type {
  AggregationComponent,
  AggregationFilter,
} from 'flow/store/engine/types';

export type AggregationObjectType = 'entity' | 'transaction';

export const AggregationFilterOperator = {
  LT: 'lt',
  LTE: 'lte',
  GT: 'gt',
  GTE: 'gte',
  EQ: 'eq',
  NE: 'ne',
  BT: 'bt',
  IN: 'in',
} as const;

export type AggregationFilterOperatorType =
  (typeof AggregationFilterOperator)[keyof typeof AggregationFilterOperator];

export type AggregationFilterUiDataType =
  | 'integer'
  | 'boolean'
  | 'string'
  | 'select'
  | 'decimal'
  | 'timestamp'
  | 'published_attribute';

export type AggregationPublishedAttribute = {
  published_attribute_token: string;
  published_attribute_destination_column: string;
  published_attribute_ui_data_type: Exclude<AggregationFilterUiDataType, 'published_attribute'>;
  published_attribute_evaluation_payload_path: string;
  published_attribute_description: string;
  published_attribute_name?: string;
  data_type?: string;
  contains_pii?: boolean;
  aggregations: AggregationType[];
  filter_operators: AggregationFilterOperatorType[];
  possible_values: string[] | null;
};

export type AggregationColumn = {
  display_name: string;
  column_slug: string;
  user_filterable: boolean;
  user_aggregable: boolean;
  aggregations: AggregationType[];
  filter_operators: AggregationFilterOperatorType[];
  ui_data_type: AggregationFilterUiDataType;
  published_attribute_metadata?: {
    published_attributes: AggregationPublishedAttribute[];
  } | null;
};

export type AggregationFilterOption = Option & {
  ui_data_type: AggregationFilterUiDataType;
  filter_operators: AggregationFilterOperatorType[];
  column_slug?: string;
  published_attribute_description?: string | null;
  published_attribute_ui_data_type?: Exclude<AggregationFilterUiDataType, 'published_attribute'>;
  published_attribute_name?: string;
  data_type?: string;
  contains_pii?: boolean;
  possible_values?: string[] | null;
};

export type AggregationColumnOption = AggregationFilterOption & {
  aggregations: AggregationType[];
};

export type AggregationConfig = {
  columns: AggregationColumn[];
  objectDisplayName: string;
  publicVersion: number;
};

export type AggregationConfigByObjectType = Partial<
Record<AggregationObjectType, AggregationConfig>
>;

export type OptionGroup<T> = {
  label: string;
  value: null;
  options: T[];
};

export type AggregationSourceData = {
  name: string;
  description: string;
  aggregation: AggregationComponent['aggregation'];
  scope: string;
  filters: AggregationFilter[];
  timeframeIntervalStart: number;
  timeframeIntervalEnd: number;
  timeframeUnit: TimeframeUnit;
};

/** Keys for aggregation fields that use the select/dropdown (handleSelectChange). */
export type AggregationSelectFieldKey =
  | keyof Pick<AggregationSourceData, 'scope' | 'timeframeUnit'>
  | 'columnSlug' | 'aggregationType';

export type AggregationValidationErrors = {
  name?: string;
  description?: string;
};
